import { useButton } from 'react-aria'
import { forwardRef, RefObject, useRef } from 'react'
import './Button.scss'

export interface ButtonProps extends React.ComponentPropsWithoutRef<'button'> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'navy' | 'quinary' | 'link'
  size?: 'normal' | 'sm'
  full?: boolean
  theme?: 'v1' | 'v2'
}

const generateButtonModifier = (modifier: string) => `ClButton--${modifier}`

/**
 * **The Thriveworks Button component**.
 ** 4 color variants, 2 themes & 2 sizes to match the Thriveworks design
 ** Takes all the same 'props' / 'attributes' as an HTML button.
 * @param variant influences the button style.
 * @param theme influences the button theme style.'v1' or 'v2'.'v1' by default.
 * @param size 'normal' or 'sm'. is 'normal' by default.
 * @param full a boolean that determines whehter width is 100%, or fit-content.
 * @param className any additional classNames you wisth to apply.
 * @author Brendan Roman
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'normal',
      full = false,
      theme = 'v1',
      onClick,
      className = '',
      children,
      disabled,
      ...props
    },
    reference
  ) => {
    const sizeVariant = generateButtonModifier(size)
    const colorVariant = generateButtonModifier(variant)
    const widthVariant = full
      ? generateButtonModifier('full')
      : generateButtonModifier('fit')

    const ref = reference ?? useRef()
    const { buttonProps } = useButton(
      props as Parameters<typeof useButton>[0],
      ref as RefObject<HTMLButtonElement>
    )

    return (
      <button
        {...buttonProps}
        onClick={onClick}
        ref={ref}
        className={`ClButton ${colorVariant} ${sizeVariant} ${widthVariant} TWTheme--${theme} ${className}`}
        type={
          (buttonProps as { type?: 'button' | 'submit' | 'reset' }).type ||
          'button'
        }
        disabled={disabled}
      >
        {children}
      </button>
    )
  }
)

export default Button
